import { get, patch, post } from "@/infra/rest";
import { useLoggedInMember } from "@/modules/auth/states";
import { ragCreateAPI } from "@/modules/sessions/requests";
import type {
  CustomGapAssessmentRagCreateRequest,
  DoraRagCreateRequest,
  FairRagCreateRequest,
  MultiFileVendorRagCreateRequest,
  NistAIRMFRagCreateRequest,
  NistCsf2RagCreateRequest,
  QARagCreateRequest,
  Soc2RagCreateRequest,
} from "@/modules/sessions/types";
import { getZaniaTeamMemberRoles } from "@/modules/team/states";
import { QueryKeys } from "@/shared/constants/QueryKeys";
import { useQuery } from "@tanstack/react-query";

export const emitRagCreate = (
  data:
    | FairRagCreateRequest
    | Soc2RagCreateRequest
    | NistCsf2RagCreateRequest
    | NistAIRMFRagCreateRequest
    | DoraRagCreateRequest
    | MultiFileVendorRagCreateRequest 
    | QARagCreateRequest
    | CustomGapAssessmentRagCreateRequest,
    

  callback?: (...args: any[]) => void,
) => {
  void ragCreateAPI(data, callback);
};

interface RAGDataRequest {
  task: string;
  output_format: string;
  response_quality: string;
  agent_session_id: string;
  use_for_ai_training?: boolean;
}

interface RAGResponse {
  success: boolean;
  message: string;
  data: {
    file_url: string;
    output_format: string;
  };
}

export const getRAGExcelFromJSON = (data: RAGDataRequest) => {
  return post<RAGDataRequest, RAGResponse>({
    url: "/v1/rag/converter",
    data,
  });
};

export const getFrameworksList = async () => {
  const response = await get<any>({
      url:  "/v0/frameworks/custom/gap_assessment",
    });
    response.mapping = response?.data.reduce((acc: any, curr: any) => {
        acc[curr.id] = curr.framework_name
        return acc
      }, {});
    return response;
};

export const useGetFrameworksListQuery= ({isEnabled}: any) => {


  const loggedInMember = useLoggedInMember();
  const memId = loggedInMember?.member_id || "";

  const {
          
          canAccessZaniaGapAssessment,
          
        } = getZaniaTeamMemberRoles(memId);
  return useQuery({
    queryKey: [QueryKeys.AllFrameworksList],
    queryFn: getFrameworksList,
    enabled: isEnabled && canAccessZaniaGapAssessment
  });
};

interface ControlSet {
  controls_set_name: string;
  controls_url: string;
  default_control_set: boolean;
  id: string;
  control_set_published: boolean;
  created_by: string;
  created_on: string;
  updated_by: string;
  updated_on: string;
  number_of_controls?: number;
}

interface ControlSetsResponse {
  success: boolean;
  control_sets: {
    multi_file_vendor_assessment: ControlSet[];
  };
}

export const getControlSets = async () => {
  const response = await get<ControlSetsResponse>({
    url: "/v0/custom-control-set",
  });
  return response.control_sets?.multi_file_vendor_assessment || [];
};

export const useGetControlSetsQuery = ({ isEnabled }: any) => {

  return useQuery({
    queryKey: [QueryKeys.ControlSets],
    queryFn: getControlSets,
    enabled: isEnabled,
    staleTime: 0, // Always consider data stale
    gcTime: 0, // Don't cache the data
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
};

interface UpdateControlSetPayload {
  controls_url: string;
  operation_type: "update_control_set_controls";
}

interface UpdateControlSetResponse {
  success: boolean;
  message: string;
  data: ControlSet;
}

export const updateControlSet = async (controlSetId: string, payload: UpdateControlSetPayload) => {
  const response = await patch<UpdateControlSetResponse>({
    url: `/v0/custom-control-set/${controlSetId}`,
    config: {
      data: payload,
    },
  });
  return response;
};
